import React from 'react';
import { AlertCircle } from 'lucide-react';
import { InlineLoadingState } from './LoadingSpinner';
import {
  SPREADSHEET_PREVIEW_MAX_COLS,
  SPREADSHEET_PREVIEW_MAX_ROWS,
  columnLetter,
  loadSpreadsheetPreview,
  type SpreadsheetPreviewKind,
  type SpreadsheetSheet,
} from '../utils/spreadsheetPreview';

type Props = {
  url: string;
  filename: string;
  kind: SpreadsheetPreviewKind;
  className?: string;
};

/** Excel-/CSV-Vorschau als Tabelle mit Spaltenbuchstaben, Zeilennummern und Blatt-Tabs. */
export function SpreadsheetViewer({ url, filename, kind, className }: Props) {
  const [sheets, setSheets] = React.useState<SpreadsheetSheet[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setError(null);
    setActive(0);
    loadSpreadsheetPreview(url, kind, filename)
      .then((loaded) => {
        if (cancelled) return;
        setSheets(loaded);
        const firstVisible = loaded.findIndex((s) => !s.hidden);
        setActive(firstVisible >= 0 ? firstVisible : 0);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [url, kind, filename]);

  if (error) {
    return (
      <div className={`flex items-start p-6 ${className ?? ''}`}>
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!sheets) {
    return (
      <div className={`grid place-items-center ${className ?? ''}`}>
        <InlineLoadingState label="Tabelle wird geladen…" />
      </div>
    );
  }

  const sheet = sheets[active];
  const truncatedRows = sheet ? sheet.totalRows > sheet.rows.length : false;
  const columns = sheet ? Array.from({ length: sheet.columnCount }, (_, i) => columnLetter(i)) : [];

  return (
    <div className={`flex min-h-0 flex-col bg-app-surface ${className ?? ''}`}>
      <div className="app-scrollbar min-h-0 flex-1 overflow-auto">
        {!sheet || sheet.rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-app-text-subtle">Dieses Blatt ist leer.</p>
        ) : (
          <table className="border-separate border-spacing-0 text-xs text-app-text">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-[2] min-w-[3rem] border-b border-r border-app-border bg-app-surface-muted" />
                {columns.map((letter) => (
                  <th
                    key={letter}
                    className="sticky top-0 z-[1] min-w-[6rem] border-b border-r border-app-border bg-app-surface-muted px-2 py-1 text-center font-medium text-app-text-subtle"
                  >
                    {letter}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sheet.rows.map((row) => (
                <tr key={row.rowNumber}>
                  <th className="sticky left-0 z-[1] border-b border-r border-app-border bg-app-surface-muted px-2 py-1 text-right font-medium text-app-text-subtle">
                    {row.rowNumber}
                  </th>
                  {columns.map((letter, i) => (
                    <td key={letter} className="border-b border-r border-app-border px-2 py-1">
                      <div className="max-w-[24rem] truncate" title={row.cells[i] || undefined}>
                        {row.cells[i] ?? ''}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {truncatedRows || (sheet && sheet.columnCount >= SPREADSHEET_PREVIEW_MAX_COLS) ? (
        <p className="shrink-0 border-t border-app-border bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          Vorschau zeigt höchstens {SPREADSHEET_PREVIEW_MAX_ROWS.toLocaleString('de-DE')} Zeilen und{' '}
          {SPREADSHEET_PREVIEW_MAX_COLS} Spalten
          {truncatedRows ? ` (Blatt hat ${sheet!.totalRows.toLocaleString('de-DE')} Zeilen)` : ''} — für alles bitte
          herunterladen.
        </p>
      ) : null}
      {/* Wie in Excel immer sichtbar, damit klar ist, welches Blatt man sieht (CSV hat keine Blätter). */}
      {kind === 'xlsx' && sheets.length > 0 ? (
        <div className="app-scrollbar flex shrink-0 gap-1 overflow-x-auto border-t border-app-border bg-app-surface-muted px-2 py-1.5">
          {sheets.map((s, i) => (
            <button
              key={`${s.name}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              className={`shrink-0 rounded-md px-3 py-1 text-xs font-medium transition ${
                i === active
                  ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-app-border'
                  : 'text-app-text-muted hover:bg-app-hover'
              } ${s.hidden ? 'italic opacity-70' : ''}`}
              title={s.hidden ? 'Ausgeblendetes Blatt' : undefined}
            >
              {s.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
